/**
 * SettingsRail — the sticky vertical nav on the left of every settings page.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ .set-rail`:
 *   - 5 groups (Business identity / Sales & documents / Cash & catalogue
 *     / People & control / Account)
 *   - 16 items, each rendered as a row: dot · label · optional pip
 *   - Active item gets a sky-tinted background + the dot fills to sky.
 *   - Built items are <Link>s; stubbed items are disabled <button>s with
 *     a "Coming soon" tooltip.
 *
 * Source of truth for the section list lives in `./lib/sections.ts`.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { cn } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import type { Permission } from "@flint/api-contract";
import { meQueryOptions } from "../../lib/session.ts";
import { SETTINGS_GROUPS, type SectionId, type PipTone } from "./lib/sections.ts";

/**
 * Map a live subscription status to the Subscription rail pip. `active` (and any
 * unknown status) shows no pip — a paying workspace should read as calm. Driven
 * by the session's `company.subscription.status` so it never goes stale.
 */
function subscriptionPipFor(status: string | undefined): { text: string; tone: PipTone } | undefined {
  switch (status) {
    case "trialing":
      return { text: "Trial", tone: "warn" };
    case "activation_required":
      return { text: "Activate", tone: "warn" };
    case "trial_expired":
      return { text: "Expired", tone: "issue" };
    case "past_due":
      return { text: "Past due", tone: "issue" };
    case "soft_suspended":
    case "hard_suspended":
      return { text: "Suspended", tone: "issue" };
    case "on_hold":
      return { text: "On hold", tone: "issue" };
    case "canceled":
      return { text: "Canceled", tone: "issue" };
    default:
      return undefined;
  }
}

const PIP_TONE_CLASS: Record<PipTone, string> = {
  info: "bg-sky-50  text-sky-700  border-sky-100",
  warn: "bg-warn-50 text-amber-800 border-warn-100",
  issue: "bg-bad-50  text-bad      border-bad-100",
};

const DOT_TONE_CLASS: Record<PipTone, string> = {
  info: "bg-sky-600",
  warn: "bg-warn",
  issue: "bg-bad",
};

export interface SettingsRailProps {
  /** Which section is currently active. Drives the highlight + aria-current. */
  active: SectionId;
}

export function SettingsRail({ active }: SettingsRailProps) {
  const me = useQuery(meQueryOptions);
  const modules = me.data?.company?.modules ?? [];
  const permissions = me.data?.company?.permissions ?? [];
  // Live pip for the Subscription row — reflects the real status, never a stale
  // hardcoded "Trial". Undefined (no pip) while the session resolves or when
  // active.
  const subscriptionPip = subscriptionPipFor(me.data?.company?.subscription?.status);
  // While the session resolves, don't hide module-gated rows (avoid a flash of
  // a missing row for the common, entitled case); enforcement is server-side.
  const sessionReady = me.data?.company != null;
  const hasModule = (module?: string): boolean =>
    !module || !sessionReady || modules.includes(module);
  // Permission gate: while the session is loading, treat every permission as
  // granted to avoid a flash of every row disabled for legitimate Admin /
  // Owner users. Once resolved, an unset `item.permission` means the row is
  // open to everyone (Profile, About & status).
  const canAccess = (perm?: Permission): boolean =>
    !perm || !sessionReady || hasPermission(permissions, perm);

  return (
    <aside
      aria-label="Settings sections"
      // Sticky on desktop; the prototype's `top: calc(var(--top-h) + 20px)`
      // is replicated via `top-5` (the page-head sits above with its own
      // padding so we don't need to reserve --top-h again here — the
      // ScrollArea is what scrolls, not window).
      className="
        bg-paper border border-line rounded-md shadow
        self-start
        lg:sticky lg:top-5 lg:max-h-[calc(100dvh-var(--top-h)-40px)]
        overflow-y-auto
      "
    >
      {SETTINGS_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.hidden && hasModule(item.module)),
      }))
        .filter((group) => group.items.length > 0)
        .map((group, idx) => (
        <div
          key={group.label}
          className={cn("p-2", idx > 0 && "border-t border-line")}
        >
          <div className="flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium uppercase tracking-wider text-muted">
            {group.label}
          </div>
          <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
            {group.items.map((item) => {
              const isActive = item.id === active;
              // The Subscription row's pip is live (from the session); all others
              // use their static config pip.
              const pip = item.id === "subscription" ? subscriptionPip : item.pip;
              const dotClass = isActive
                ? "bg-sky-600"
                : pip
                  ? DOT_TONE_CLASS[pip.tone]
                  : "bg-line-2";
              const baseRow = cn(
                "flex h-9 w-full items-center gap-2.5 overflow-hidden rounded-sm px-2 text-left text-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
              );

              const permitted = canAccess(item.permission);

              if (item.built && permitted) {
                return (
                  <li key={item.id}>
                    <Link
                      to={`/settings/${item.id}` as never}
                      className={cn(
                        baseRow,
                        isActive
                          ? "bg-sky-50 text-sky-700 font-semibold"
                          : "text-ink-2 hover:bg-bg hover:text-ink",
                      )}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span
                        className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotClass)}
                        aria-hidden
                      />
                      <span className="flex-1 min-w-0 truncate">{item.label}</span>
                      {pip ? (
                        <span
                          className={cn(
                            "inline-flex items-center px-1.5 py-px rounded-sm text-xs font-semibold uppercase tracking-wider border",
                            PIP_TONE_CLASS[pip.tone],
                          )}
                        >
                          {pip.text}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              }

              // Disabled row. Two disabled variants share the same visual:
              //   1. Stub item (`built: false`)                → "coming soon" tooltip.
              //   2. Built but user lacks permission           → "Permission denied"
              //                                                  tooltip + lock icon on the right
              //                                                  so the reason is obvious.
              const isPermissionLocked = item.built && !permitted;
              const tooltip = !item.built
                ? `${item.label} — coming soon`
                : "Permission denied";

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    disabled
                    title={tooltip}
                    className={cn(
                      baseRow,
                      "w-full text-left text-muted opacity-70 cursor-not-allowed bg-transparent border-none",
                    )}
                  >
                    <span
                      className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotClass)}
                      aria-hidden
                    />
                    <span className="flex-1 min-w-0 truncate">{item.label}</span>
                    {isPermissionLocked ? (
                      <Lock size={12} strokeWidth={2} aria-hidden className="shrink-0 text-muted" />
                    ) : pip ? (
                      <span
                        className={cn(
                          "inline-flex items-center px-1.5 py-px rounded-sm text-xs font-semibold uppercase tracking-wider border opacity-60",
                          PIP_TONE_CLASS[pip.tone],
                        )}
                      >
                        {pip.text}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </aside>
  );
}
